import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { DAYS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload } from "lucide-react";

const TYPES = ["Hair Salon", "Beauty Salon", "Aesthetics Clinic", "Massage Therapy", "Nail Salon", "Lash & Brow", "Barber", "Spa", "Other"];
const defaultHours = () => { const h = {}; for (let d = 0; d < 7; d++) h[d] = { closed: d >= 5, open: "09:00", close: "17:00" }; return h; };

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [form, setForm] = useState({
    name: "", owner_name: user?.name || "", phone: "", address: "",
    business_type: "Hair Salon", slug: "", opening_hours: defaultHours(),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setHour = (d, k, v) => setForm((f) => ({ ...f, opening_hours: { ...f.opening_hours, [d]: { ...f.opening_hours[d], [k]: v } } }));
  const autoSlug = form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const submit = async (importAfter = false) => {
    setLoading(true);
    try {
      let logo_url = null;
      await api.post("/business", { ...form, slug: autoSlug });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const { data } = await api.post("/business/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        logo_url = data.logo_url;
      }
      await refresh();
      toast.success("Business created — welcome to Get Booked In!");
      navigate(importAfter === true ? "/services?import=1" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-9 w-9 rounded-xl bg-stone-900 flex items-center justify-center text-[#D4A373] font-bold">G</div>
          <span className="font-bold text-lg">Get Booked In</span>
        </div>
        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-[#D4A373]" : "bg-stone-200"}`} />
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6 sm:p-8 animate-fade-up">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h1 className="font-extrabold text-2xl">Tell us about your business</h1>
                <p className="text-sm text-stone-600 mt-1">This appears on your booking page.</p>
              </div>
              <div><Label>Business name</Label>
                <Input data-testid="onboarding-business-name" value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-lg" placeholder="Bella Hair Studio" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Owner name</Label><Input data-testid="onboarding-owner-name" value={form.owner_name} onChange={set("owner_name")} className="mt-1.5 h-11 rounded-lg" /></div>
                <div><Label>Phone</Label><Input data-testid="onboarding-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 h-11 rounded-lg" placeholder="07123 456789" /></div>
              </div>
              <div><Label>Business type</Label>
                <Select value={form.business_type} onValueChange={(v) => setForm((f) => ({ ...f, business_type: v }))}>
                  <SelectTrigger data-testid="onboarding-business-type" className="mt-1.5 h-11 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Address</Label><Textarea data-testid="onboarding-address" value={form.address} onChange={set("address")} className="mt-1.5 rounded-lg" rows={2} /></div>
              <div className="flex justify-end">
                <Button data-testid="onboarding-next-1" disabled={!form.name} onClick={() => setStep(2)} className="rounded-full bg-stone-900 hover:bg-stone-800">Next <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div><h1 className="font-extrabold text-2xl">Logo & booking link</h1>
                <p className="text-sm text-stone-600 mt-1">Optional logo and your public booking URL.</p></div>
              <div>
                <Label>Logo</Label>
                <div className="mt-1.5 flex items-center gap-4">
                  <div className="h-16 w-16 rounded-xl border border-stone-200 bg-stone-50 overflow-hidden flex items-center justify-center">
                    {logoPreview ? <img src={logoPreview} alt="" className="h-full w-full object-cover" /> : <Upload className="h-5 w-5 text-stone-400" />}
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" data-testid="onboarding-logo-input" className="hidden" onChange={onLogo} />
                    <span className="inline-flex items-center px-4 py-2 rounded-full border border-stone-300 text-sm hover:bg-stone-50">Upload image</span>
                  </label>
                </div>
              </div>
              <div>
                <Label>Booking URL</Label>
                <div className="mt-1.5 flex items-center rounded-lg border border-stone-300 overflow-hidden h-11">
                  <span className="px-3 text-sm text-stone-500 bg-stone-50 h-full flex items-center border-r border-stone-200">getbookedin.co.uk/</span>
                  <input data-testid="onboarding-slug" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} placeholder={autoSlug} className="flex-1 px-3 h-full outline-none text-sm font-mono" />
                </div>
                <p className="text-xs text-stone-500 mt-1.5">Your page: getbookedin.co.uk/{autoSlug || "your-salon"}</p>
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep(1)} className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
                <Button data-testid="onboarding-next-2" onClick={() => setStep(3)} className="rounded-full bg-stone-900 hover:bg-stone-800">Next <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div><h1 className="font-extrabold text-2xl">Opening hours</h1>
                <p className="text-sm text-stone-600 mt-1">Set when clients can book. You can change this later.</p></div>
              <div className="space-y-2">
                {DAYS.map((day, d) => (
                  <div key={d} className="flex items-center gap-3 py-1.5">
                    <div className="w-24 text-sm font-medium">{day}</div>
                    <Switch checked={!form.opening_hours[d].closed} onCheckedChange={(v) => setHour(d, "closed", !v)} data-testid={`onboarding-day-${d}-toggle`} />
                    {form.opening_hours[d].closed ? (
                      <span className="text-sm text-stone-400">Closed</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input type="time" value={form.opening_hours[d].open} onChange={(e) => setHour(d, "open", e.target.value)} className="border border-stone-300 rounded-lg px-2 py-1 text-sm" />
                        <span className="text-stone-400">–</span>
                        <input type="time" value={form.opening_hours[d].close} onChange={(e) => setHour(d, "close", e.target.value)} className="border border-stone-300 rounded-lg px-2 py-1 text-sm" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={() => setStep(2)} className="rounded-full"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button data-testid="onboarding-finish-import" variant="outline" onClick={() => submit(true)} disabled={loading} className="rounded-full">Finish & upload your price list</Button>
                  <Button data-testid="onboarding-finish" onClick={() => submit(false)} disabled={loading} className="rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#C29263]">
                    {loading ? "Creating…" : "Finish & open dashboard"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
